import React, { useState, useEffect } from 'react';
import { Button, Form, Input, Message, Modal, Dropdown } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import PositionedModal from 'common/components/PositionedModal';
import { getCurrentOrganization, isCurrentUserStaffOrSuperAdmin, isCurrentMemberOrgAdmin, MEMBER_ROLE } from '../../../../common/selectors/auth';

import Tabs from 'common/components/Tabs';
import userContextSlice from '../../../../common/slices/userContextSlice';
import MemberEditCategoriesTab from './MemberEditCategoriesTab';

import { CreateEmployee } from '../../../../store/generated/portal/employee/employees';
import { ValidateEmail } from 'common/utils/EmailUtils';

interface MemberCreateModalProps {
  open: boolean;
  onSave: (member: CreateEmployee) => void;
  onClose: () => void;
  loading: boolean;
  error?: string;
  member?: CreateEmployee;
}

const MemberCreateModal: React.FC<MemberCreateModalProps> = ({
  open,
  onSave,
  onClose,
  loading,
  error,
  member,
}) => {
  // Get auth state
  const isAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const currentOrganization = useSelector(getCurrentOrganization);
  const isAccessLevelDisabled = !isAdmin && (currentOrganization?.tier ?? 0) < 2;

  const [formData, setFormData] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    team_external_id?: string;
    account_type?: number;
    survey_status?: number;
    status?: number;
  }>({
    first_name: '',
    last_name: '',
    email: '',
    team_external_id: '',
    status: 1, // Default to active
    account_type: MEMBER_ROLE.REGULAR_USER, // Default to regular user
    survey_status: 1, // Default to survey participant
  });

  const [emailError, setEmailError] = useState<string | null>(null);

  // Update form data when member prop changes
  useEffect(() => {
    if (member) {
      setFormData({
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        email: member.email,
        team_external_id: (member as any).team_external_id || '',
        account_type: member.account_type,
      });
    }
  }, [member]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };


  const handleDropdownChange = (e: React.SyntheticEvent, data: any) => {
    setFormData(prev => ({
      ...prev,
      [data.name]: data.value,
    }));
  };

  // reset form data when modal opens
  const demographicFields = useSelector(userContextSlice.selectors.getDemographicFields);
  const demographicFieldsArray = demographicFields ? Object.values(demographicFields) : [];

  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (open) {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        team_external_id: '',
        account_type: MEMBER_ROLE.REGULAR_USER,
        status: 1,
        survey_status: 1,
      });
      setEmailError(null);
      setActiveTab('details');
    }
  }, [open]);

  const handleSubmit = () => {
    const email = formData.email.trim();

    if (!formData.first_name || !formData.last_name || !email) {
      return;
    }

    if (!ValidateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    // Build demographic_data from demographic fields (support both 'role' and 'demographic_role' keying)
    const demographic_data: Record<string, any> = {};
    demographicFieldsArray.forEach((field: any) => {
      const fieldKey = field.name === 'role' ? 'demographic_role' : field.name;
      const val = (formData as any)[fieldKey] ?? (formData as any)[field.name];
      if (val !== undefined && val !== '') {
        demographic_data[field.name] = val;
      }
    });

    const payload: any = {
      ...formData,
      email,
    };

    if (Object.keys(demographic_data).length > 0) {
      payload.demographic_data = demographic_data;
    }

    onSave(payload as CreateEmployee);
  };

  const handleCancel = () => {
    onClose();
  };

  // If user doesn't have permission, show unauthorized message
  if (!isAdmin && !isOrgAdmin) {
    return (
      <PositionedModal open={open} onClose={onClose} size="small">
        <Modal.Header>Unauthorized</Modal.Header>
        <Modal.Content>
          <Message negative>
            <Message.Header>Access Denied</Message.Header>
            <p>You do not have permission to manage organization members.</p>
          </Message>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Close</Button>
        </Modal.Actions>
      </PositionedModal>
    );
  }

  const detailsContent = (
    <Form>
      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>First Name</label>
        <Form.Field required style={{ flex: 1 }}>
          <Input
            placeholder="Enter first name"
            value={formData.first_name}
            onChange={e => handleInputChange(e, 'first_name')}
            error={!formData.first_name}
            fluid
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Last Name</label>
        <Form.Field required style={{ flex: 1 }}>
          <Input
            placeholder="Enter last name"
            value={formData.last_name}
            onChange={e => handleInputChange(e, 'last_name')}
            error={!formData.last_name}
            fluid
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Email</label>
        <Form.Field required style={{ flex: 1 }} error={!!emailError || !formData.email}>
          <Input
            placeholder="Enter email"
            type="email"
            value={formData.email}
            onChange={e => handleInputChange(e, 'email')}
            onBlur={() => {
              if (!formData.email) {
                setEmailError('Email is required.');
              } else if (!ValidateEmail(formData.email)) {
                setEmailError('Please enter a valid email address.');
              } else {
                setEmailError(null);
              }
            }}
            fluid
            error={!!emailError || !formData.email}
          />
          {emailError && (
            <div style={{ color: '#9f3a38', marginTop: 4, fontSize: 12 }}>
              {emailError}
            </div>
          )}

        </Form.Field>
      </Form.Group>


      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Access Level</label>
        <Form.Field required style={{ flex: 1 }}>
          <Dropdown
            placeholder="Select access level"
            fluid
            selection
            name="account_type"
            value={formData.account_type}
            onChange={handleDropdownChange}
            disabled={isAccessLevelDisabled}
            options={[
              { key: 'member', value: MEMBER_ROLE.REGULAR_USER, text: 'Member' },
              { key: 'team-leader', value: MEMBER_ROLE.TEAM_LEADER, text: 'Team Leader' },
              { key: 'org-admin', value: MEMBER_ROLE.ORG_ADMIN, text: 'Organization Admin' },
            ]}
          />
        </Form.Field>
      </Form.Group>



      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Survey Participation</label>
        <Form.Field required style={{ flex: 1 }}>
          <Dropdown
            placeholder="Select survey participation status"
            fluid
            selection
            name="survey_status"
            value={formData.survey_status}
            onChange={handleDropdownChange}
            options={[
              { key: 1, value: 1, text: 'Survey Participant' },
              { key: 2, value: 2, text: 'Does Not Participate' },
            ]}
          />
        </Form.Field>
      </Form.Group>

      <Form.Group inline>
        <label style={{ minWidth: '170px' }}>Team External ID</label>
        <Form.Field style={{ flex: 1 }}>
          <Input
            placeholder="Enter team external ID"
            value={formData.team_external_id || ''}
            onChange={e => handleInputChange(e, 'team_external_id')}
            fluid
          />
        </Form.Field>
      </Form.Group>
    </Form>
  );

  const demographicsContent = (
    <MemberEditCategoriesTab
      formData={formData}
      demographicFields={demographicFieldsArray}
      onDropdownChange={handleDropdownChange}
    />
  );

  return (
    <PositionedModal open={open} onClose={onClose} size="small">
      <Modal.Header>{member ? 'Edit Member' : 'Add Member'}</Modal.Header>
      <Modal.Content>
        {error && <Message negative content={error} />}

        <Tabs
          borderbottom
          tabs={[
            { name: 'details', label: 'Details', content: detailsContent },
            { name: 'demographics', label: 'Demographics', content: demographicsContent },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          positive
          onClick={handleSubmit}
          loading={loading}
          disabled={!formData.first_name || !formData.last_name || !formData.email || !!emailError}
        >
          {member ? 'Save Changes' : 'Add Member'}
        </Button>
      </Modal.Actions>
    </PositionedModal>
  );
};

export default MemberCreateModal;
